import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { mismosDatos } from '../../../assets/shared/utility';

/** Identifica al comprobante aunque el listado se recargue y el objeto cambie (refresco de Pedidos, nueva última acción). */
export const claveComprobante = (comprobante) =>
  comprobante ? `${comprobante.idSucursal}|${comprobante.idSistema}|${comprobante.idComproba}|${comprobante.nroReferencia}` : null;

const sinDatos = { clave: null, pedido: null, datos: null, error: null };

/**
 * Pide datos de un comprobante con una acción del store estilo callback (comprobante, success, fail) y descarta las
 * respuestas de un comprobante anterior. Se pide una sola vez por comprobante (y version): al desactivarse y volver
 * a activarse (cambiar de pestaña) se reusa lo que ya llegó.
 * - activo: false para no pedir todavía (ej. pestaña no visible).
 * - version: cambiarla vuelve a pedir (refresco). Mientras tanto se siguen mostrando los datos anteriores y, si la
 *   respuesta es igual, se deja el mismo objeto (no se redibuja nada); si el refresco falla, quedan los anteriores.
 * - alRecibir(...respuesta): arma los datos con lo que devuelve success (por defecto el primer argumento). Con
 *   alRecibir no se compara: cada respuesta reemplaza a la anterior.
 * - alLiberar(datos): libera los datos reemplazados o al desmontar (ej. blob URL).
 */
const useDatosComprobante = (comprobante, cargar, { activo = true, version, alRecibir, alLiberar } = {}) => {
  const dispatch = useDispatch();
  const clave = claveComprobante(comprobante);
  const pedido = clave ? `${clave}|${version}` : null;
  const [estado, setEstado] = useState(sinDatos);

  // El comprobante y las funciones se leen al pedir; el efecto sólo depende del pedido.
  const ultimo = useRef();
  ultimo.current = { comprobante, cargar, alRecibir, alLiberar };
  const pedidoEnCurso = useRef(null);

  useEffect(() => {
    // Sin comprobante (ej. modal cerrado) se olvida lo pedido: al volver a abrirlo se pide de nuevo.
    if (!pedido) {
      pedidoEnCurso.current = null;
      setEstado((anterior) => (anterior.pedido ? sinDatos : anterior));
      return;
    }
    if (!activo || pedidoEnCurso.current === pedido) return;

    pedidoEnCurso.current = pedido;
    const { comprobante: actual, cargar: pedir, alRecibir: armar } = ultimo.current;
    // Otro comprobante: se vacía. Mismo comprobante (refresco): se sigue mostrando lo que hay.
    setEstado((anterior) => (anterior.clave === clave ? anterior : { ...sinDatos, clave, pedido }));
    dispatch(
      pedir(
        actual,
        (...respuesta) => {
          if (pedidoEnCurso.current !== pedido) return;
          const nuevos = armar ? armar(...respuesta) : respuesta[0];
          setEstado((anterior) =>
            !armar && anterior.clave === clave && mismosDatos(anterior.datos, nuevos)
              ? { ...anterior, pedido }
              : { clave, pedido, datos: nuevos, error: null }
          );
        },
        (mensaje) =>
          pedidoEnCurso.current === pedido &&
          setEstado((anterior) => (anterior.clave === clave && anterior.datos ? { ...anterior, pedido } : { clave, pedido, datos: null, error: mensaje }))
      )
    );
  }, [pedido, clave, activo, dispatch]);

  // Al reemplazarse los datos o desmontar, se liberan los anteriores.
  useEffect(() => {
    const datos = estado.datos;
    return () => {
      if (datos && ultimo.current.alLiberar) ultimo.current.alLiberar(datos);
    };
  }, [estado.datos]);

  useEffect(
    () => () => {
      pedidoEnCurso.current = null;
    },
    []
  );

  const vigente = estado.clave === clave;
  const datos = vigente ? estado.datos : null;
  const error = vigente ? estado.error : null;
  return { datos, error, cargando: !!pedido && activo && !datos && !error };
};

export default useDatosComprobante;
